import React, { useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import { DesignMode, getDesignTokens } from './designTokens';

export type CrystalTableColumn<Row extends object> = {
  key: keyof Row;
  label: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  render?: (row: Row) => React.ReactNode;
};

type SortDirection = 'asc' | 'desc';

export type CrystalTableProps<Row extends object> = {
  mode?: DesignMode;
  columns: CrystalTableColumn<Row>[];
  rows: Row[];
  sortable?: boolean;
  emptyMessage?: string;
};

export const CrystalTable = <Row extends object>({
  mode = 'light',
  columns,
  rows,
  sortable = true,
  emptyMessage = 'No data available.'
}: CrystalTableProps<Row>) => {
  const tokens = getDesignTokens(mode);
  const [sortKey, setSortKey] = useState<keyof Row | null>(
    sortable
      ? (columns.find((column) => column.sortable !== false)?.key ?? null)
      : null
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const sortedRows = useMemo(() => {
    if (!sortable || !sortKey) return rows;

    return [...rows].sort((firstRow, secondRow) => {
      const firstValue = firstRow[sortKey];
      const secondValue = secondRow[sortKey];
      const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return (firstValue - secondValue) * directionMultiplier;
      }

      return (
        String(firstValue).localeCompare(String(secondValue)) *
        directionMultiplier
      );
    });
  }, [rows, sortDirection, sortKey, sortable]);

  const handleSort = (column: CrystalTableColumn<Row>) => {
    if (!sortable || column.sortable === false) return;

    if (sortKey === column.key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(column.key);
    setSortDirection('asc');
  };

  return (
    <TableContainer
      sx={{
        position: 'relative',
        overflowX: 'auto',
        borderRadius: tokens.radius.lg,
        border: `1px solid ${
          mode === 'light' ? 'rgba(29,29,31,0.08)' : 'rgba(255,255,255,0.14)'
        }`,
        background:
          mode === 'light'
            ? 'linear-gradient(180deg, #ffffff, #f7f7f9)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
        boxShadow:
          mode === 'light'
            ? 'inset 0 1px 0 rgba(255,255,255,0.98), 0 16px 44px rgba(0,0,0,0.075)'
            : 'inset 0 1.5px 0 rgba(255,255,255,0.14), 0 14px 34px rgba(0,0,0,0.22)',
        backdropFilter: 'blur(12px) saturate(135%)',
        WebkitBackdropFilter: 'blur(12px) saturate(135%)'
      }}
    >
      <Table sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              const isActiveSort = sortable && sortKey === column.key;
              const canSort = sortable && column.sortable !== false;

              return (
                <TableCell
                  key={String(column.key)}
                  align={column.align ?? 'left'}
                  onClick={() => handleSort(column)}
                  sx={{
                    py: 1.8,
                    px: 2,
                    borderBottom: `1px solid ${tokens.colors.border}`,
                    color: tokens.colors.textMuted,
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: 0,
                    textTransform: 'uppercase',
                    cursor: canSort ? 'pointer' : 'default',
                    background:
                      mode === 'light'
                        ? 'rgba(245,245,247,0.74)'
                        : 'rgba(255,255,255,0.045)',
                    userSelect: 'none'
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent:
                        column.align === 'right'
                          ? 'flex-end'
                          : column.align === 'center'
                            ? 'center'
                            : 'flex-start',
                      gap: 0.5,
                      width: '100%'
                    }}
                  >
                    {column.label}
                    {canSort &&
                      (isActiveSort && sortDirection === 'desc' ? (
                        <ArrowDownwardRoundedIcon sx={{ fontSize: 15 }} />
                      ) : (
                        <ArrowUpwardRoundedIcon
                          sx={{
                            fontSize: 15,
                            opacity: isActiveSort ? 1 : 0.28
                          }}
                        />
                      ))}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{
                  py: 5,
                  color: tokens.colors.textMuted,
                  borderBottom: 0,
                  textAlign: 'center'
                }}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row, rowIndex) => (
              <TableRow
                key={`${String(row[columns[0].key])}-${rowIndex}`}
                sx={{
                  transition: 'background 160ms ease',
                  '&:hover': {
                    background:
                      mode === 'light'
                        ? 'rgba(0,113,227,0.055)'
                        : 'rgba(65,200,196,0.07)'
                  }
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    align={column.align ?? 'left'}
                    sx={{
                      py: 1.7,
                      px: 2,
                      color: tokens.colors.text,
                      fontSize: 14,
                      fontWeight: column.key === columns[0].key ? 900 : 700,
                      borderBottom:
                        rowIndex === sortedRows.length - 1
                          ? 0
                          : `1px solid ${tokens.colors.border}`
                    }}
                  >
                    {column.render
                      ? column.render(row)
                      : String(row[column.key] ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <Typography
        sx={{
          px: 2,
          py: 1.3,
          color: tokens.colors.textMuted,
          borderTop: `1px solid ${tokens.colors.border}`,
          fontSize: 12,
          fontWeight: 800
        }}
      >
        {sortable ? 'Sorting enabled' : 'Sorting disabled'} · {rows.length} rows
      </Typography>
    </TableContainer>
  );
};
